import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageSquare, ClipboardList, Gamepad2, Ticket, ArrowRight, Sparkles } from "lucide-react";
import PushNotificationToggle from "@/components/PushNotificationToggle";
import WeeklyLeaderboard from "@/components/WeeklyLeaderboard";
import HabitantBadges from "@/components/HabitantBadges";
import ResidenceVerificationCard from "@/components/ResidenceVerificationCard";
import CniVerificationCard from "@/components/CniVerificationCard";

export default function HabitantDashboard() {
  const { user } = useAuth();
  const [vouchers, setVouchers] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [mine, setMine] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/vouchers/mine").then((r) => setVouchers(r.data)).catch(() => {}),
      api.get("/surveys").then((r) => setSurveys(r.data)).catch(() => {}),
      api.get("/quizzes").then((r) => setQuizzes(r.data)).catch(() => {}),
      api.get("/feedback/mine").then((r) => setMine(r.data)).catch(() => {}),
    ]);
  }, []);

  const pendingSurveys = surveys.filter((s) => !s.responded).length;
  const pendingQuizzes = quizzes.filter((q) => !q.played).length;

  return (
    <div className="space-y-8" data-testid="habitant-dashboard">
      <header className="grid lg:grid-cols-12 gap-6 items-center">
        <div className="lg:col-span-8">
          <div className="phenix-pill bg-pink-100 text-pink-600 mb-3">
            <Sparkles size={11} strokeWidth={2.5} /> Salut !
          </div>
          <h1 className="heading-display text-3xl lg:text-4xl">
            Bonjour <span className="text-pink-500">{user.pseudo}</span> 👋
          </h1>
          <p className="mt-2 text-sm text-plum-700/70 max-w-md">
            {pendingSurveys + pendingQuizzes > 0
              ? `${pendingSurveys} sondage${pendingSurveys > 1 ? "s" : ""} et ${pendingQuizzes} quiz vous attendent.`
              : "Vous êtes à jour. Belle journée !"}
          </p>
        </div>
        <aside className="lg:col-span-4">
          <div className="phenix-card p-4 bg-gradient-to-br from-plum-600 to-plum-800 text-white border-0">
            <div className="text-[10px] font-bold uppercase tracking-widest text-pink-200">Mes points</div>
            <div className="font-display font-extrabold text-4xl mt-1">{user.points || 0}</div>
            <div className="text-[11px] text-plum-100">+10 par sondage · +50 par quiz</div>
          </div>
        </aside>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="habitant-stats">
        <Stat icon="💬" color="bg-pink-100 text-pink-600" label="Remontées" value={mine.length} />
        <Stat icon="🗳️" color="bg-plum-100 text-plum-600" label="Sondages ouverts" value={pendingSurveys} />
        <Stat icon="🎮" color="bg-sun-100 text-sun-700" label="Quiz dispo" value={pendingQuizzes} />
        <Stat icon="🎁" color="bg-mint-100 text-mint-600" label="Bons" value={vouchers.length} />
      </section>

      <section className="grid lg:grid-cols-3 gap-4">
        <Tile to="/remontees" icon={MessageSquare} title="Envoyer une remontée" desc="Problème, idée, suggestion" color="bg-pink-400" />
        <Tile to="/sondages" icon={ClipboardList} title="Répondre aux sondages" desc="Donnez votre avis sur la ville" color="bg-plum-600" />
        <Tile to="/jeux" icon={Gamepad2} title="Jouer & gagner" desc="Quiz récompensés par des bons" color="bg-sun-400" featured />
      </section>

      {/* PWA push notifications */}
      <PushNotificationToggle />

      {/* Justificatif de domicile (co-voiturage) */}
      <ResidenceVerificationCard />

      {/* CNI — Avantages Citoyens */}
      <CniVerificationCard />

      {/* Mes badges */}
      <HabitantBadges />

      {/* Classement hebdomadaire des contributeurs */}
      <WeeklyLeaderboard />

      <section data-testid="habitant-activity-feed">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display font-bold text-xl text-plum-900">Mon activité</h2>
          <Link to="/remontees" className="text-xs font-semibold text-plum-600 hover:text-pink-500">Tout voir →</Link>
        </div>
        {mine.length === 0 ? (
          <div className="phenix-card p-6 text-center text-plum-700/50 text-sm">
            Aucune activité pour l'instant.
          </div>
        ) : (
          <div className="space-y-2">
            {mine.slice(0, 5).map((f) => (
              <div key={f.id} className="phenix-card p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-pink-100 text-pink-600 grid place-items-center shrink-0">
                  <MessageSquare size={15} strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-plum-900 text-sm truncate">{f.title}</div>
                  <div className="text-[11px] text-plum-700/60 capitalize">
                    {f.type} · {new Date(f.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                  </div>
                </div>
                <span className={`phenix-pill ${
                  f.status === "answered" ? "bg-mint-100 text-mint-600" : "bg-cream-100 text-plum-700/60"
                }`}>
                  {f.status === "answered" ? "Répondue" : "En attente"}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ icon, color, label, value }) {
  return (
    <div className="phenix-card p-4">
      <div className={`w-9 h-9 rounded-xl grid place-items-center ${color} text-base mb-2`}>{icon}</div>
      <div className="font-display font-extrabold text-2xl text-plum-900">{value}</div>
      <div className="text-[11px] text-plum-700/60">{label}</div>
    </div>
  );
}

function Tile({ to, icon: Icon, title, desc, color, featured }) {
  return (
    <Link
      to={to}
      className={`phenix-card phenix-card-hover p-5 group flex flex-col ${featured ? "bg-gradient-to-br from-sun-100 to-cream-50 border-sun-200" : ""}`}
    >
      <div className={`${color} text-white w-10 h-10 rounded-xl grid place-items-center mb-4 shadow-sm`}>
        <Icon size={17} strokeWidth={2.2} />
      </div>
      <h3 className="font-display font-bold text-base text-plum-900 mb-1">{title}</h3>
      <p className="text-[13px] text-plum-700/70 flex-1">{desc}</p>
      <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-plum-600 group-hover:gap-2 transition-all">
        Ouvrir <ArrowRight size={12} />
      </div>
    </Link>
  );
}
